import React, { useState } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import inboundApi from '../../config/inboundApi';
import logoColor from '../../assets/logo-color.svg';

const pageStyle = {
  background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
};

const cardStyle = {
  width: 420,
  background: '#fff',
  borderRadius: 24,
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  border: 'none',
  padding: '2.5rem',
  position: 'relative',
  zIndex: 1,
};

const titleStyle = {
  fontFamily: "'Inter', sans-serif",
  fontWeight: 800,
  fontSize: '2rem',
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: '0.25rem',
};

const subtitleStyle = {
  color: '#64748b',
  fontSize: '0.9rem',
  fontWeight: 500,
  margin: 0,
};

const labelStyle = {
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: '#475569',
};

const inputStyle = {
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  padding: '0.625rem 1rem',
  fontSize: '0.9rem',
};

const btnStyle = {
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  border: 'none',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  fontSize: '0.9rem',
  borderRadius: 12,
  padding: '0.75rem',
  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
};

export default function Login() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [needsSubdomain, setNeedsSubdomain] = useState(false);
  const [availableSubdomains, setAvailableSubdomains] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = { email: email.trim(), password };
      if (subdomain) payload.subdomain = subdomain.trim();
      await inboundApi.post('/auth/login', payload);
      await refreshUser();
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.subdomains) {
        setNeedsSubdomain(true);
        setAvailableSubdomains(detail.subdomains);
        setError('This email exists in multiple organizations. Please select your subdomain.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div className="text-center mb-4">
          <img src={logoColor} alt="TopCX" style={{ height: 48, marginBottom: 12 }} />
          <h3 style={titleStyle}>TopCX Portal</h3>
          <p style={subtitleStyle}>Sign in to your account</p>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleEmailSubmit}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Email</Form.Label>
            <Form.Control
              style={inputStyle}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Password</Form.Label>
            <Form.Control
              style={inputStyle}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              required
            />
          </Form.Group>

          {needsSubdomain && (
            <Form.Group className="mb-3">
              <Form.Label style={labelStyle}>Organization</Form.Label>
              <Form.Select
                style={inputStyle}
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                required
              >
                <option value="">Select organization...</option>
                {availableSubdomains.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Button type="submit" className="w-100" disabled={loading} style={btnStyle}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </Form>
      </div>
    </div>
  );
}
